// components/SectionDivider.tsx - WITH SCROLL INTERACTIVE EFFECTS
import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';

interface SectionDividerProps {
  variant?: 'scroll-grow' | 'scroll-reveal' | 'scroll-stretch' | 'magnetic' | 'parallax' | 'particles' | 'ripple';
  color?: 'cyan' | 'purple' | 'white' | 'blue' | 'multi';
  thickness?: 'thin' | 'medium' | 'thick';
  spacing?: 'tight' | 'normal' | 'wide';
  className?: string;
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

const SectionDivider: React.FC<SectionDividerProps> = ({ 
  variant = 'scroll-grow',
  color = 'cyan',
  thickness = 'medium',
  spacing = 'normal',
  className = '',
  scrollContainerRef
}) => {
  const dividerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const isInViewport = useInView(dividerRef, { once: false, amount: 0.5 });
  
  // Use scroll container if provided, otherwise use window
  const { scrollYProgress } = useScroll({
    target: scrollContainerRef || undefined,
    offset: ["start start", "end start"]
  });

  // Transform scroll progress to width
  const widthProgress = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const springWidth = useSpring(widthProgress, { stiffness: 100, damping: 30 });
  
  // Particle effect states
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; size: number }>>([]);

  // Thickness mapping
  const thicknessMap = {
    thin: 'h-px',
    medium: 'h-1',
    thick: 'h-2'
  };

  // Spacing mapping
  const spacingMap = {
    tight: 'my-4',
    normal: 'my-8',
    wide: 'my-12'
  };

  // Color mapping
  const colorClasses = {
    cyan: 'bg-cyan-400 text-cyan-400 border-cyan-400',
    purple: 'bg-purple-400 text-purple-400 border-purple-400',
    white: 'bg-white text-white border-white',
    blue: 'bg-blue-400 text-blue-400 border-blue-400',
    multi: 'bg-gradient-to-r from-cyan-400 via-purple-400 to-blue-400'
  };

  // Effect for particles
  useEffect(() => {
    if (variant === 'particles' && isInViewport) {
      const newParticles = Array.from({ length: 15 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 1
      }));
      setParticles(newParticles);
    }
  }, [variant, isInViewport]);

  const baseClasses = `${spacingMap[spacing]} ${className}`;

  const variants = {
    // Grows from center as you scroll
    'scroll-grow': (
      <motion.div 
        ref={dividerRef}
        className={`relative overflow-hidden ${baseClasses}`}
        initial={{ opacity: 0 }}
        animate={isInViewport ? { opacity: 1 } : { opacity: 0 }}
        onViewportEnter={() => setIsInView(true)}
        onViewportLeave={() => setIsInView(false)}
      >
        <div className="flex justify-center items-center">
          <motion.div 
            className={`${thicknessMap[thickness]} rounded-full ${
              color === 'multi' 
                ? 'bg-gradient-to-r from-cyan-400 via-purple-400 to-blue-400' 
                : colorClasses[color].split(' ')[0]
            }`}
            initial={{ width: 0, opacity: 0 }}
            animate={isInViewport ? { width: '80%', opacity: 1 } : { width: 0, opacity: 0 }}
            transition={{ 
              width: { duration: 1, type: "spring", stiffness: 100 },
              opacity: { duration: 0.5 }
            }}
          />
        </div>
      </motion.div>
    ),

    // Reveals from left to right with scroll progress
    'scroll-reveal': (
      <motion.div 
        ref={dividerRef}
        className={`relative ${baseClasses}`}
        style={{ width: '100%' }}
      >
        <div className="relative h-px bg-white/10">
          <motion.div 
            className={`absolute top-0 left-0 ${thicknessMap[thickness]} ${
              color === 'multi' 
                ? 'bg-gradient-to-r from-cyan-400 via-purple-400 to-blue-400' 
                : colorClasses[color].split(' ')[0]
            }`}
            style={{ 
              width: useTransform(scrollYProgress, [0, 1], ['0%', '100%'])
            }}
            initial={{ scaleX: 0 }}
            animate={isInViewport ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>
      </motion.div>
    ),

    // Stretches like a rubber band when scrolled into view
    'scroll-stretch': (
      <motion.div 
        ref={dividerRef}
        className={`relative overflow-hidden ${baseClasses}`}
        initial={{ opacity: 0 }}
        animate={isInViewport ? { opacity: 1 } : { opacity: 0 }}
      >
        <motion.div 
          className={`mx-auto ${thicknessMap[thickness]} rounded-full ${
            color === 'multi' 
              ? 'bg-gradient-to-r from-cyan-400 via-purple-400 to-blue-400' 
              : colorClasses[color].split(' ')[0]
          }`}
          initial={{ width: 0, scaleX: 0 }}
          animate={isInViewport ? { width: '100%', scaleX: 1 } : { width: 0, scaleX: 0 }}
          transition={{ 
            width: { 
              type: "spring", 
              stiffness: 200, 
              damping: 20,
              delay: 0.1
            },
            scaleX: { duration: 0.8, ease: "easeOut" }
          }}
          whileInView={{
            scaleX: [1, 1.02, 0.98, 1],
            transition: { 
              scaleX: { duration: 2, repeat: Infinity, repeatType: "reverse" }
            }
          }}
        />
      </motion.div>
    ),

    // Magnetic effect - divider follows scroll direction
    'magnetic': (
      <motion.div 
        ref={dividerRef}
        className={`relative ${baseClasses}`}
        style={{ 
          scaleX: useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.2, 1]),
          rotate: useTransform(scrollYProgress, [0, 1], [0, 5])
        }}
      >
        <div className="flex justify-center">
          <motion.div 
            className={`${thicknessMap[thickness]} w-3/4 rounded-full relative ${
              color === 'multi' 
                ? 'bg-gradient-to-r from-cyan-400 via-purple-400 to-blue-400' 
                : colorClasses[color].split(' ')[0]
            }`}
            animate={{
              boxShadow: isInViewport 
                ? ['0 0 0px rgba(255,255,255,0.1)', '0 0 10px rgba(255,255,255,0.3)', '0 0 0px rgba(255,255,255,0.1)']
                : '0 0 0px rgba(255,255,255,0)'
            }}
            transition={{ 
              boxShadow: { duration: 2, repeat: Infinity }
            }}
          >
            <motion.div 
              className="absolute inset-0 bg-white/20 rounded-full"
              animate={{ 
                x: ['-100%', '100%', '-100%']
              }}
              transition={{
                x: { duration: 3, repeat: Infinity, ease: "linear" }
              }}
            />
          </motion.div>
        </div>
      </motion.div>
    ),

    // Parallax effect - moves at different speed than scroll
    'parallax': (
      <motion.div 
        ref={dividerRef}
        className={`relative overflow-hidden ${baseClasses}`}
        style={{ 
          y: useTransform(scrollYProgress, [0, 1], [0, 50]),
          opacity: useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 1])
        }}
      >
        <div className="relative h-2 overflow-hidden">
          <motion.div 
            className={`absolute inset-0 ${
              color === 'multi' 
                ? 'bg-gradient-to-r from-cyan-400/20 via-purple-400/40 to-blue-400/20' 
                : `bg-gradient-to-r from-transparent via-${color === 'cyan' ? 'cyan' : color === 'purple' ? 'purple' : color === 'blue' ? 'blue' : 'white'}-400/40 to-transparent`
            }`}
            animate={isInViewport ? { x: ['-100%', '100%'] } : { x: '-100%' }}
            transition={{ 
              x: { duration: 4, repeat: Infinity, ease: "linear" }
            }}
          />
          <motion.div 
            className={`absolute inset-0 ${
              color === 'multi' 
                ? 'bg-gradient-to-r from-blue-400/20 via-cyan-400/40 to-purple-400/20' 
                : `bg-gradient-to-r from-transparent via-${color === 'cyan' ? 'cyan' : color === 'purple' ? 'purple' : color === 'blue' ? 'blue' : 'white'}-400/30 to-transparent`
            }`}
            animate={isInViewport ? { x: ['100%', '-100%'] } : { x: '100%' }}
            transition={{ 
              x: { duration: 3, repeat: Infinity, ease: "linear", delay: 0.5 }
            }}
          />
        </div>
      </motion.div>
    ),

    // Particles that float up when in view
    'particles': (
      <motion.div 
        ref={dividerRef}
        className={`relative ${baseClasses}`}
        initial={{ opacity: 0 }}
        animate={isInViewport ? { opacity: 1 } : { opacity: 0 }}
        onViewportEnter={() => setIsInView(true)}
      >
        <div className="relative h-8 flex items-center justify-center">
          {/* Main line */}
          <div className={`absolute h-px w-full ${
            color === 'multi' 
              ? 'bg-gradient-to-r from-transparent via-cyan-400/30 via-purple-400/30 via-blue-400/30 to-transparent' 
              : `bg-gradient-to-r from-transparent via-${color === 'cyan' ? 'cyan' : color === 'purple' ? 'purple' : color === 'blue' ? 'blue' : 'white'}-400/30 to-transparent`
          }`}></div>
          
          {/* Floating particles */}
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className={`absolute rounded-full ${
                color === 'cyan' ? 'bg-cyan-400/50' :
                color === 'purple' ? 'bg-purple-400/50' :
                color === 'white' ? 'bg-white/30' :
                color === 'blue' ? 'bg-blue-400/50' :
                particle.id % 3 === 0 ? 'bg-cyan-400/40' :
                particle.id % 3 === 1 ? 'bg-purple-400/40' :
                'bg-blue-400/40'
              }`}
              style={{
                left: `${particle.x}%`,
                top: '50%',
                width: particle.size,
                height: particle.size,
              }}
              animate={isInView ? {
                y: [0, -40, 0],
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              } : {}}
              transition={{
                duration: 2 + Math.random(),
                delay: particle.id * 0.1,
                repeat: Infinity,
                repeatDelay: Math.random() * 2
              }}
            />
          ))}
        </div>
      </motion.div>
    ),

    // Ripple effect that expands from center
    'ripple': (
      <motion.div 
        ref={dividerRef}
        className={`relative ${baseClasses}`}
        onClick={() => setIsInView(!isInView)}
      >
        <div className="flex items-center justify-center">
          <motion.div 
            className={`relative ${thicknessMap[thickness]} w-16 rounded-full ${
              color === 'multi' 
                ? 'bg-gradient-to-r from-cyan-400 via-purple-400 to-blue-400' 
                : colorClasses[color].split(' ')[0]
            }`}
            animate={isInViewport ? { 
              width: ['16px', '100%', '16px'],
              opacity: [0.5, 1, 0.5]
            } : { width: '16px', opacity: 0.5 }}
            transition={{ 
              width: { duration: 3, repeat: Infinity, ease: "easeInOut" },
              opacity: { duration: 3, repeat: Infinity }
            }}
          >
            {/* Ripple circles */}
            <motion.div 
              className={`absolute inset-0 rounded-full border ${
                color === 'cyan' ? 'border-cyan-400/30' :
                color === 'purple' ? 'border-purple-400/30' :
                color === 'white' ? 'border-white/30' :
                color === 'blue' ? 'border-blue-400/30' :
                'border-cyan-400/30'
              }`}
              animate={isInViewport ? {
                scale: [1, 2, 1],
                opacity: [0.5, 0, 0.5]
              } : {}}
              transition={{ 
                scale: { duration: 2, repeat: Infinity, delay: 0.5 },
                opacity: { duration: 2, repeat: Infinity, delay: 0.5 }
              }}
            />
            <motion.div 
              className={`absolute inset-0 rounded-full border ${
                color === 'cyan' ? 'border-cyan-400/20' :
                color === 'purple' ? 'border-purple-400/20' :
                color === 'white' ? 'border-white/20' :
                color === 'blue' ? 'border-blue-400/20' :
                'border-cyan-400/20'
              }`}
              animate={isInViewport ? {
                scale: [1, 3, 1],
                opacity: [0.3, 0, 0.3]
              } : {}}
              transition={{ 
                scale: { duration: 3, repeat: Infinity },
                opacity: { duration: 3, repeat: Infinity }
              }}
            />
          </motion.div>
        </div>
      </motion.div>
    )
  };

  return variants[variant] || variants['scroll-grow'];
};

export default SectionDivider;